import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
  TableContainer
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Plan } from '../../types';
import { createPlan, deletePlan, getPlans, updatePlan } from '../../utils/planApi';

type FormState = {
  id?: string;
  name: string;
  description: string;
  price: string;
  initialCredits: string;
  duration: 'month' | 'year' | 'free';
  maxMedia: string;
};

const emptyForm: FormState = {
  name: '',
  description: '',
  price: '',
  initialCredits: '',
  duration: 'month',
  maxMedia: '10'
};

const PlansManagement: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadPlans = async () => {
    setLoading(true);
    try {
      const data = await getPlans();
      setPlans(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlans();
  }, []);

  const handleOpenCreate = () => {
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const handleOpenEdit = (p: Plan) => {
    setForm({
      id: p.id,
      name: p.name,
      description: p.description,
      price: String(p.price),
      initialCredits: String(p.initialCredits),
      duration: p.duration,
      maxMedia: String(p.maxMedia || 10)
    });
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const priceError = useMemo(() => {
    if (!form.price) return 'Preço é obrigatório';
    const n = Number(form.price);
    if (Number.isNaN(n) || n < 0) return 'Preço inválido';
    return '';
  }, [form.price]);

  const initialCreditsError = useMemo(() => {
    if (!form.initialCredits) return 'Créditos iniciais são obrigatórios';
    const n = Number(form.initialCredits);
    if (Number.isNaN(n) || n < 0) return 'Créditos iniciais inválidos';
    return '';
  }, [form.initialCredits]);

  const maxMediaError = useMemo(() => {
    if (!form.maxMedia) return 'Limite de mídias é obrigatório';
    const n = Number(form.maxMedia);
    if (Number.isNaN(n) || (n < -1)) return 'Limite de mídias inválido';
    return '';
  }, [form.maxMedia]);

  const canSave = form.name.trim() && !priceError && !initialCreditsError && !maxMediaError && (form.duration === 'month' || form.duration === 'year' || form.duration === 'free');

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        price: Number(form.price),
        initialCredits: Number(form.initialCredits),
        duration: form.duration,
        maxMedia: Number(form.maxMedia)
      } as const;

      if (form.id) {
        await updatePlan(form.id, payload);
      } else {
        await createPlan(payload);
      }
      setDialogOpen(false);
      await loadPlans();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este plano?')) return;
    await deletePlan(id);
    await loadPlans();
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={700}>Planos</Typography>
        <Button variant="contained" sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined }} startIcon={<AddIcon />} onClick={handleOpenCreate}>Novo Plano</Button>
      </Stack>

      <Card>
        <CardContent>
          <TableContainer sx={{
            overflowX: 'auto',
            width: '100%',
            '&::-webkit-scrollbar': {
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f1f1f1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.mode === 'dark' ? '#555' : '#c1c1c1',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' ? '#777' : '#a8a8a8',
              },
            },
          }}>
            <Table size="small" sx={{
              minWidth: 600,
              width: '100%'
            }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 120 }}>Nome</TableCell>
                  <TableCell sx={{ minWidth: 150 }}>Descrição</TableCell>
                  <TableCell align="right" sx={{ minWidth: 100 }}>Preço (R$)</TableCell>
                  <TableCell align="right" sx={{ minWidth: 120 }}>Créditos Iniciais</TableCell>
                  <TableCell sx={{ minWidth: 100 }}>Duração</TableCell>
                  <TableCell align="right" sx={{ minWidth: 100 }}>Ações</TableCell>
                </TableRow>
              </TableHead>
            <TableBody>
              {plans.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell>{p.name}</TableCell>
                  <TableCell>{p.description}</TableCell>
                  <TableCell align="right">{p.price.toFixed(2)}</TableCell>
                  <TableCell align="right">{p.initialCredits}</TableCell>
                  <TableCell>
                    {p.duration === 'free'
                      ? 'Grátis'
                      : p.duration === 'month'
                        ? 'Mensal'
                        : 'Anual'}
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 0.5,
                      justifyContent: 'flex-end',
                      flexDirection: isMobile ? 'row' : 'row'
                    }}>
                      <IconButton onClick={() => handleOpenEdit(p)} size="small"><EditIcon /></IconButton>
                      <IconButton onClick={() => handleDelete(p.id)} size="small" color="error"><DeleteIcon /></IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {plans.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={6} align="center">Nenhum plano cadastrado</TableCell>
                </TableRow>
              )}
            </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{form.id ? 'Editar Plano' : 'Novo Plano'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 0.5 }}>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <TextField
                label="Nome"
                name="name"
                value={form.name}
                onChange={handleChange}
                fullWidth
                required
              />
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <TextField
                label="Descrição"
                name="description"
                value={form.description}
                onChange={handleChange}
                fullWidth
                multiline
                minRows={3}
              />
            </Box>
            <Box>
              <TextField
                label="Preço (R$)"
                name="price"
                value={form.price}
                onChange={handleChange}
                fullWidth
                required
                error={!!priceError}
                helperText={priceError || ' '}
              />
            </Box>
            <Box>
              <TextField
                label="Créditos Iniciais"
                name="initialCredits"
                value={form.initialCredits}
                onChange={handleChange}
                fullWidth
                required
                type="number"
                error={!!initialCreditsError}
                helperText={initialCreditsError || ' '}
              />
            </Box>
            <Box>
              <TextField
                label="Limite de Mídias"
                name="maxMedia"
                value={form.maxMedia}
                onChange={handleChange}
                fullWidth
                required
                type="number"
                error={!!maxMediaError}
                helperText={maxMediaError || 'Use -1 para ilimitado'}
              />
            </Box>
            <Box>
              <TextField
                select
                label="Duração"
                name="duration"
                value={form.duration}
                onChange={handleChange}
                fullWidth
              >
                <MenuItem value="free">Grátis</MenuItem>
                <MenuItem value="month">Mensal</MenuItem>
                <MenuItem value="year">Anual</MenuItem>
              </TextField>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button onClick={handleSave} sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined }} disabled={!canSave || saving} variant="contained">Salvar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlansManagement;


